import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
    AssistantReservationAccountState,
    AssistantReservationTimeState,
    AssistantResource,
    AssistantServiceOrderState,
    AssistantSummaryReservation,
} from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { DateTimePart, DateTimeService, LocalizedEnum, lt, useTimezone } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    Card,
    CardContent,
    Divider,
    SegmentedControl,
    SegmentedControlItem,
    Stack,
    Tag,
    Typography,
} from '@optimus-web/core';
import ReservationCheckOutAction from '../../../reservationModule/components/SmartDetail/ReservationCheckOutAction';
import { useCustomerReservationsQuery } from '../../query/useCustomerReservations';
import { DateTimeLabel } from '../DateTimeLabel';
import { ActiveReservationProps } from '../hooks/useSummaryCustomerHook';
import { LabelValue } from '../LabelValue';
import { reservationTimeSateTextKeyVariant } from '../reservationTimeSateTextKeyVariant';
import { SummaryAccordion } from '../styles';
import { CustomerReservationCheckinContainer } from './CustomerReservationCheckinContainer';

interface CustomerReservationsProps {
    customerId: string;
    setActiveReservationState: Dispatch<SetStateAction<ActiveReservationProps>>;
}

enum ReservationFilter {
    Active = 'active',
    Past = 'past',
    Cancelled = 'cancelled',
}

const activeStates = [
    AssistantReservationTimeState.CheckedIn,
    AssistantReservationTimeState.ToBeCheckedIn,
    AssistantReservationTimeState.ToBeCheckedOut,
    AssistantReservationTimeState.CheckInMissed,
    AssistantReservationTimeState.CheckOutMissed,
    AssistantReservationTimeState.Future,
    AssistantReservationTimeState.ToBeConfirmed,
    AssistantReservationTimeState.ConfirmationMissed,
];

const getFilteredReservations = (
    classification: ReservationFilter | null,
    reservations: AssistantSummaryReservation[]
) => {
    if (classification === null) {
        return [];
    }

    return reservations.filter((reservation) => {
        switch (classification) {
            case ReservationFilter.Cancelled:
                return reservation.serviceOrderState === AssistantServiceOrderState.Canceled;
            case ReservationFilter.Active:
                return reservation?.timeState !== undefined
                    ? activeStates.includes(reservation.timeState) &&
                          reservation.serviceOrderState !== AssistantServiceOrderState.Canceled
                    : false;

            case ReservationFilter.Past:
                return (
                    reservation.timeState === AssistantReservationTimeState.Past &&
                    reservation.serviceOrderState !== AssistantServiceOrderState.Canceled
                );
            default:
                return false;
        }
    });
};

const reservationFilters: { value: ReservationFilter; label: Mews.TextKey }[] = [
    { value: ReservationFilter.Active, label: TextKey.AssistantSummaryGuestReservationFilterActive },
    { value: ReservationFilter.Past, label: TextKey.AssistantSummaryGuestReservationFilterPast },
    { value: ReservationFilter.Cancelled, label: TextKey.AssistantSummaryGuestReservationFilterCanceled },
];

const getFirstFilterWithResults = (reservations: AssistantSummaryReservation[]) => {
    const activeReservations = getFilteredReservations(ReservationFilter.Active, reservations);
    if (activeReservations.length > 0) {
        return ReservationFilter.Active;
    }

    const pastReservations = getFilteredReservations(ReservationFilter.Past, reservations);
    if (pastReservations.length > 0) {
        return ReservationFilter.Past;
    }

    const cancelledReservations = getFilteredReservations(ReservationFilter.Cancelled, reservations);
    if (cancelledReservations.length > 0) {
        return ReservationFilter.Cancelled;
    }

    return null;
};

export const CustomerReservations = ({ customerId, setActiveReservationState }: CustomerReservationsProps) => {
    const { data: result } = useCustomerReservationsQuery(customerId);
    const reservations: AssistantSummaryReservation[] = useMemo(() => result?.reservations || [], [result]);

    const [selectedReservationFilter, setSelectedReservationFilter] = useState<ReservationFilter | null>(null);
    const [filteredReservations, setFilteredReservations] = useState<AssistantSummaryReservation[]>([]);

    useEffect(() => {
        if (reservations.length > 0) {
            setSelectedReservationFilter(getFirstFilterWithResults(reservations));
            setActiveReservationState((prev) => ({
                ...prev,
                activeReservations: getFilteredReservations(ReservationFilter.Active, reservations),
                hasPastReservations: reservations.some((item) => item.timeState === AssistantReservationTimeState.Past),
                hasMoreThanOneActiveReservation: prev.activeReservations.length > 1,
                reservation: prev.activeReservations[0] ?? undefined,
            }));
        }
    }, [reservations, setActiveReservationState]);

    useEffect(() => {
        if (selectedReservationFilter) {
            const filtered = getFilteredReservations(selectedReservationFilter, reservations);
            setFilteredReservations(filtered);
        }
    }, [reservations, selectedReservationFilter]);

    const handleSegmentedControlChange = (value: ReservationFilter | string) => {
        const classification = reservationFilters.find((c) => c.value === value) || reservationFilters[0];
        setSelectedReservationFilter(classification.value);

        const filtered = getFilteredReservations(classification.value, reservations);
        setFilteredReservations(filtered);
    };

    if (reservations.length < 1) {
        return null;
    }

    return (
        <Card padding="0" overflowHidden>
            <CardContent padding="200">
                <Stack vertical spacing="150">
                    <Stack spacing="100">
                        <Typography textStyle="bodyMediumStrong" variant="primary" as="div">
                            {lt('ReservationPlural')}
                        </Typography>
                        <Typography textStyle="bodyMedium" variant="secondary" as="div">
                            {reservations.length}
                        </Typography>
                    </Stack>
                    <SegmentedControl onChange={(_, value) => handleSegmentedControlChange(value)} size="small">
                        <SegmentedControlItem
                            key={reservationFilters[0].value}
                            label={lt(reservationFilters[0].label)}
                            value={reservationFilters[0].value}
                            checked={selectedReservationFilter === reservationFilters[0].value}
                            disabled={getFilteredReservations(ReservationFilter.Active, reservations).length === 0}
                        />
                        <SegmentedControlItem
                            key={reservationFilters[1].value}
                            label={lt(reservationFilters[1].label)}
                            value={reservationFilters[1].value}
                            checked={selectedReservationFilter === reservationFilters[1].value}
                            disabled={getFilteredReservations(ReservationFilter.Past, reservations).length === 0}
                        />
                        <SegmentedControlItem
                            key={reservationFilters[2].value}
                            label={lt(reservationFilters[2].label)}
                            value={reservationFilters[2].value}
                            checked={selectedReservationFilter === reservationFilters[2].value}
                            disabled={getFilteredReservations(ReservationFilter.Cancelled, reservations).length === 0}
                        />
                    </SegmentedControl>
                </Stack>
            </CardContent>
            <CardContent padding="0">
                <Divider />
            </CardContent>
            <CardContent padding="200">
                <Stack vertical spacing="100">
                    {filteredReservations.map((reservation, index) => (
                        <Stack vertical key={reservation.id}>
                            <Reservation
                                index={index}
                                length={filteredReservations.length}
                                reservation={reservation}
                                hasMoreThanOneActiveReservation={
                                    getFilteredReservations(ReservationFilter.Active, reservations).length > 1
                                }
                            />
                            {index < filteredReservations.length - 1 && <Divider />}
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

interface ReservationProps {
    index: number;
    length: number;
    reservation: AssistantSummaryReservation;
    hasMoreThanOneActiveReservation: boolean;
}

const Reservation = ({ reservation, hasMoreThanOneActiveReservation }: ReservationProps) => {
    const [accordionExpanded, setAccordionExpanded] = useState(false);
    const { textKey, tagVariant } = reservationTimeSateTextKeyVariant[reservation.timeState];
    return (
        <SummaryAccordion
            key={reservation.id}
            expanded={accordionExpanded}
            headerPadding={{ top: '100', bottom: '150', left: '50', right: '50' }}
            bodyPadding="50"
            onClick={() => setAccordionExpanded((expanded) => !expanded)}
            withBodyBorder={false}
            withHeaderBorder={false}
            endSlot={<Tag variant={tagVariant}>{lt(textKey)}</Tag>}
            title={<LabelValue label={lt('Confirmation')} value={reservation.number} />}
            body={
                <ReservationBody
                    id={reservation.id}
                    startUtc={reservation.startUtc}
                    endUtc={reservation.endUtc}
                    assignedResourceName={reservation.resource?.name}
                    groupName={reservation.groupName}
                    resource={reservation.resource}
                    accountState={reservation.accountState}
                    timeState={reservation.timeState}
                    hasMoreThanOneActiveReservation={hasMoreThanOneActiveReservation}
                />
            }
        />
    );
};

interface ReservationBodyProps {
    id: string;
    startUtc: string;
    endUtc: string;
    resource: AssistantResource | undefined;
    assignedResourceName: string | undefined;
    groupName: string;
    accountState: AssistantReservationAccountState | undefined;
    timeState: AssistantReservationTimeState;
    hasMoreThanOneActiveReservation: boolean;
}

const ReservationBody = ({
    startUtc,
    endUtc,
    resource,
    groupName,
    accountState,
    timeState,
    id,
    hasMoreThanOneActiveReservation,
}: ReservationBodyProps) => {
    const timezone = useTimezone();
    const startDate = DateTimeService.fromUtcString(startUtc, timezone);
    const endDate = DateTimeService.fromUtcString(endUtc, timezone);
    const nights = DateTimeService.diff(DateTimePart.Day, { roundDown: true })(endDate, startDate);

    const startDateLabel = <DateTimeLabel dateUtc={startUtc} />;
    const endDateLabel = <DateTimeLabel dateUtc={endUtc} />;
    return (
        <Stack vertical spacing="150">
            <Divider />
            <Stack vertical spacing="100">
                {accountState && (
                    <LabelValue
                        label={lt('Type')}
                        value={<LocalizedEnum prefix="ReservationAccountState" value={accountState} />}
                    />
                )}
                <LabelValue label={lt('Group')} value={groupName} />
                <LabelValue label={lt('CheckIn')} value={startDateLabel} />
                <LabelValue label={lt('Checkout')} value={endDateLabel} />
                <LabelValue label={lt('Night')} value={nights.toString()} />
            </Stack>
            <Divider borderStyle="dotted" />
            {resource && (
                <Stack vertical={false} spacing="150" style={{ paddingRight: '40px' }}>
                    {/* <Toggle onIcon={IconName.Lock} offBackgroundIcon={IconName.Unlock}/> */}
                    <LabelValue label={lt('Space')} value={resource.name} />
                    <Tag variant="basicSubtle">{resource.state}</Tag>
                </Stack>
            )}
            {(timeState === AssistantReservationTimeState.ToBeCheckedIn ||
                timeState === AssistantReservationTimeState.CheckInMissed) &&
                hasMoreThanOneActiveReservation && (
                    <CustomerReservationCheckinContainer
                        variant="secondary"
                        reservationId={id}
                        onUpdate={() => {}}
                        withCheckInButton
                        assignedResourceId={resource?.id}
                    />
                )}
            {(timeState === AssistantReservationTimeState.ToBeCheckedOut ||
                timeState === AssistantReservationTimeState.CheckOutMissed) &&
                hasMoreThanOneActiveReservation && <ReservationCheckOutAction reservationId={id} onUpdate={() => {}} />}
            <Typography textStyle="bodyExtraSmall" as="div">
                {UnicodeEntity.NoBreakSpace}
            </Typography>
        </Stack>
    );
};
